import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import URI from 'urijs';
import { omit } from 'lodash';
import { url as urlUtils } from '@clubmed/utils';
import moment from 'moment';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { ID_MODAL_HEADER__BE_REFRESH_PROPOSAL } from 'Components/utils/identifiers/constants';
import { t } from 'Src/i18n';
import { Modal } from 'Components/frameworks/modal/Modal';
import { Button } from 'Components/frameworks/button/Button';
type RefreshProposalProps = {
  refreshedProposal: RefreshedProposal;
  resort: ProductWithAccommodations;
  selectedProposal: Proposal;
};

const useRefreshProposal = (refreshedProposal: RefreshedProposal, selectedProposal: Proposal) => {
  const { proposal, errors } = refreshedProposal;
  const [hasError] = useState(
    errors &&
      errors.length &&
      errors.some((error) =>
        ['noAccommodation', 'noTransport', 'noService'].includes(error.errorKey),
      ),
  );
  const [hasDifferentPrice] = useState(
    proposal &&
      selectedProposal &&
      proposal.id === selectedProposal.id &&
      proposal.price.total.value !== selectedProposal.price.total.value,
  );
  return {
    hasError,
    hasDifferentPrice,
  };
};

export function RefreshProposalModal({
  refreshedProposal,
  resort,
  selectedProposal,
}: RefreshProposalProps) {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const { image: imageUrl, destination, name } = resort;
  const { hasError, hasDifferentPrice } = useRefreshProposal(refreshedProposal, selectedProposal);
  const { trackClickEvent } = useTrackEvent();
  const errorMessage = hasError
    ? t('booking.saveMyCriteria.refreshPopIn.noAvailability')
    : t('booking.saveMyCriteria.refreshPopIn.updatedProposal');

  const redirectToResort = () => {
    trackClickEvent(TRACK_EVENT_NAMES.CLICK_NEW_PROPOSALS, {
      detail_click: 'error_%400',
    });
    const resortUri = new URI(resort?.href)
      .query({
        ...omit(router.query, [
          'bookingStep',
          'proposalIds',
          'selectedProposalId',
          'date',
          'duration',
        ]),
        date: moment(selectedProposal.arrivalDate).format('YYYY-MM-DD'),
        duration: selectedProposal.duration,
      })
      .toString();
    router.push(urlUtils.normalizeUrl(resortUri));
  };

  const refreshPage = () => {
    trackClickEvent(TRACK_EVENT_NAMES.CLICK_NEW_PROPOSALS, {
      detail_click: 'new_price',
    });
    window.location.reload();
  };

  const buttonText = hasError ? t('common.chooseNewCriteria') : t('common.continue');
  const buttonAction = hasError ? redirectToResort : refreshPage;
  useEffect(() => {
    if (hasError || hasDifferentPrice) {
      setIsOpen(true);
    }
  }, [hasError, hasDifferentPrice]);

  if (!hasError && !hasDifferentPrice) {
    return null;
  }

  return isOpen ? (
    <Modal
      ariaLabel={t('booking.saveMyCriteria.refreshPopIn.title')}
      ariaLabelledby={ID_MODAL_HEADER__BE_REFRESH_PROPOSAL}
      closeLabel={t('common.close')}
      onClose={buttonAction}
      size="small"
    >
      <div className="overflow-hidden" data-testid="RefreshProposalModal">
        <div className="relative h-228 overflow-hidden">
          {!!imageUrl && <img src={imageUrl} className="w-full" alt="" />}

          <div className="absolute inset-0 flex items-end justify-center bg-[linear-gradient(180deg,rgba(36,36,36,0)_50%,rgba(36,36,36,0.8)_100%)] text-center text-white">
            <div className="mb-20">
              <p className="text-20 font-semibold">{name}</p>
              <p className="text-16 font-normal">{destination.country}</p>
            </div>
          </div>
        </div>

        <div className="m-25 text-center">
          <div className="mb-10 font-brand text-24 text-black">
            {t('booking.saveMyCriteria.refreshPopIn.title')}
          </div>
          <div className="p-20 text-14 text-black">{errorMessage}</div>
          <Button
            className="mb-15 mt-30 min-w-200"
            data-testid="pauseOptionModalButton"
            onClick={buttonAction}
          >
            {buttonText}
          </Button>
        </div>
      </div>
    </Modal>
  ) : null;
}
